import React, { useEffect, useMemo, useState } from "react";
import {
    Button, Card, Empty, Input, Popconfirm, Space, Table, Tag, Typography, message,
} from "antd";
import {
    PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined,
    SearchOutlined, FileDoneOutlined, ReloadOutlined,
} from "@ant-design/icons";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import { PARAMETERS } from "../../../variables/constants";

const { Text, Title } = Typography;
const PRIMARY = "#242166";
const TYPE = PARAMETERS.motifReglement; // "MOTIF_REGLEMENT"

/**
 * Gestion des MOTIFS DE RÈGLEMENT (paramètres sinistres). Les motifs sont des Parametre de
 * type MOTIF_REGLEMENT ; on liste, ajoute, modifie et supprime proprement via /api/parametre.
 */
const MotifsReglement = () => {
    const [motifs, setMotifs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const [nouveau, setNouveau] = useState("");
    const [adding, setAdding] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [savingId, setSavingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const charger = () => {
        setLoading(true);
        axiosInstance.get(`/api/parametre/type/${TYPE}`)
            .then((res) => setMotifs(Array.isArray(res.data) ? res.data : []))
            .catch(() => setMotifs([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        charger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const existe = (lib, exclureId = null) =>
        motifs.some((m) => m.parametreId !== exclureId
            && (m.libelleParametre || "").trim().toLowerCase() === lib.trim().toLowerCase());

    const ajouter = () => {
        const lib = (nouveau || "").trim();
        if (!lib) return;
        if (existe(lib)) { message.warning("Ce motif existe déjà."); return; }
        setAdding(true);
        axiosInstance.post("/api/parametre",
            JSON.stringify({ typeParametre: TYPE, libelleParametre: lib }),
            { headers: { "Content-Type": "application/json" } })
            .then(() => { setNouveau(""); message.success("Motif ajouté."); charger(); })
            .catch(() => message.error("Erreur lors de l'ajout du motif."))
            .finally(() => setAdding(false));
    };

    const demarrerEdition = (motif) => {
        setEditingId(motif.parametreId);
        setEditValue(motif.libelleParametre || "");
    };

    const annulerEdition = () => {
        setEditingId(null);
        setEditValue("");
    };

    const enregistrerEdition = (motif) => {
        const lib = (editValue || "").trim();
        if (!lib) return;
        if (existe(lib, motif.parametreId)) { message.warning("Ce motif existe déjà."); return; }
        setSavingId(motif.parametreId);
        // PUT /api/parametre : objet complet (l'id est dans le corps).
        axiosInstance.put("/api/parametre",
            JSON.stringify({ ...motif, libelleParametre: lib, typeParametre: TYPE }),
            { headers: { "Content-Type": "application/json" } })
            .then(() => { message.success("Motif modifié."); annulerEdition(); charger(); })
            .catch(() => message.error("Erreur lors de la modification."))
            .finally(() => setSavingId(null));
    };

    const supprimer = (motif) => {
        setDeletingId(motif.parametreId);
        axiosInstance.delete(`/api/parametre/${motif.parametreId}`)
            .then(() => { message.success("Motif supprimé."); charger(); })
            .catch(() => message.error("Erreur lors de la suppression."))
            .finally(() => setDeletingId(null));
    };

    const filtres = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = q
            ? motifs.filter((m) => (m.libelleParametre || "").toLowerCase().includes(q))
            : motifs;
        return [...rows].sort((a, b) =>
            (a.libelleParametre || "").localeCompare(b.libelleParametre || ""));
    }, [motifs, search]);

    const columns = [
        {
            title: "#",
            key: "index",
            width: 60,
            align: "center",
            render: (_, __, i) => <Text type="secondary">{i + 1}</Text>,
        },
        {
            title: "Libellé du motif",
            dataIndex: "libelleParametre",
            key: "libelleParametre",
            render: (v, record) => (
                editingId === record.parametreId ? (
                    <Input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onPressEnter={() => enregistrerEdition(record)}
                        style={{ maxWidth: 460 }}
                    />
                ) : (
                    <span style={{ fontWeight: 500, color: "#111827" }}>{v || "—"}</span>
                )
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 220,
            align: "right",
            render: (_, record) => (
                editingId === record.parametreId ? (
                    <Space>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={savingId === record.parametreId}
                            onClick={() => enregistrerEdition(record)}
                            style={{ background: PRIMARY }}
                        >
                            Enregistrer
                        </Button>
                        <Button icon={<CloseOutlined />} onClick={annulerEdition}>Annuler</Button>
                    </Space>
                ) : (
                    <Space>
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => demarrerEdition(record)}
                        >
                            Modifier
                        </Button>
                        <Popconfirm
                            title="Supprimer ce motif ?"
                            description="Cette action est définitive."
                            okText="Supprimer"
                            okButtonProps={{ danger: true }}
                            cancelText="Annuler"
                            onConfirm={() => supprimer(record)}
                        >
                            <Button danger icon={<DeleteOutlined />} loading={deletingId === record.parametreId}>
                                Supprimer
                            </Button>
                        </Popconfirm>
                    </Space>
                )
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PARAMETRES_SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1890ff 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 20, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#fff" }}>
                                        <FileDoneOutlined style={{ marginRight: 8 }} />Motifs de règlement
                                    </Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Ajouter, modifier et supprimer les motifs proposés à l'encaissement des règlements sinistres
                                    </Text>
                                </div>
                                <Input
                                    allowClear
                                    prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.7)" }} />}
                                    placeholder="Rechercher un motif…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ maxWidth: 320, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8 }}
                                    styles={{ input: { background: "transparent", color: "#fff" } }}
                                />
                            </div>

                            {/* Ajout d'un motif */}
                            <Card bordered={false} style={{ borderRadius: 14, marginBottom: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                    <span style={{ width: 44, height: 44, borderRadius: 11, background: `${PRIMARY}14`, color: PRIMARY, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                                        <PlusOutlined />
                                    </span>
                                    <div style={{ flex: 1, minWidth: 240 }}>
                                        <div style={{ fontWeight: 700, color: "#111827", fontSize: 15 }}>Nouveau motif</div>
                                        <Text type="secondary" style={{ fontSize: 13 }}>Saisissez un libellé clair et concis.</Text>
                                    </div>
                                    <Space.Compact style={{ minWidth: 320, flex: "0 1 460px" }}>
                                        <Input
                                            placeholder="Ex. Règlement des honoraires d'expertise"
                                            value={nouveau}
                                            onChange={(e) => setNouveau(e.target.value)}
                                            onPressEnter={ajouter}
                                        />
                                        <Button type="primary" icon={<PlusOutlined />} loading={adding} onClick={ajouter} style={{ background: PRIMARY }}>
                                            Ajouter
                                        </Button>
                                    </Space.Compact>
                                </div>
                            </Card>

                            {/* Liste des motifs */}
                            <Card
                                bordered={false}
                                style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                title={<span style={{ fontWeight: 700 }}>Motifs enregistrés <Tag style={{ marginLeft: 6 }}>{motifs.length}</Tag></span>}
                                extra={<Button icon={<ReloadOutlined />} onClick={charger} loading={loading}>Rafraîchir</Button>}
                            >
                                <Table
                                    rowKey="parametreId"
                                    columns={columns}
                                    dataSource={filtres}
                                    loading={loading}
                                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (t) => `${t} motif(s)` }}
                                    locale={{ emptyText: <Empty description="Aucun motif enregistré" /> }}
                                />
                            </Card>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default MotifsReglement;
